import * as THREE from 'three';
import { C, gravityScaleAt } from '../game/constants';
import { World } from '../game/World';

/**
 * The mining ship: a VERTICAL lander built from primitives — nose cone up,
 * engine bell + flame at the bottom, side tanks, landing legs. It leans into
 * horizontal travel (left lean when flying left) like a booster on descent.
 */
export class Ship {
  readonly group = new THREE.Group();      // root: position only
  private readonly hull = new THREE.Group(); // carries attitude rotation
  vel = new THREE.Vector2();
  /** Attitude in radians: 0 = nose up, positive = tipped left (CCW). */
  angle = 0;
  landed = false;
  private readonly flame: THREE.Mesh;
  private readonly retroFlame: THREE.Mesh;
  private readonly flameMat: THREE.MeshBasicMaterial;

  constructor() {
    const hullMat = new THREE.MeshLambertMaterial({ color: 0x3a86ff });
    const darkMat = new THREE.MeshLambertMaterial({ color: 0x27334d });
    const glassMat = new THREE.MeshLambertMaterial({ color: 0xa8e7ff });
    const accentMat = new THREE.MeshLambertMaterial({ color: 0xe8e4da });

    // Vertical stack: engine bell → body → nose cone
    const body = new THREE.Mesh(new THREE.CylinderGeometry(0.85, 1.0, 2.6, 10), hullMat);
    body.position.y = 0.4;
    const nose = new THREE.Mesh(new THREE.ConeGeometry(0.85, 1.3, 10), accentMat);
    nose.position.y = 2.35;
    const bell = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.8, 0.7, 10), darkMat);
    bell.position.y = -1.25;

    // Cockpit window on the body front (toward camera)
    const window_ = new THREE.Mesh(new THREE.SphereGeometry(0.34, 10, 8), glassMat);
    window_.position.set(0, 0.95, 0.78);
    window_.scale.z = 0.55;

    // Side tanks — give the silhouette width and 3D depth
    for (const sz of [-0.85, 0.85]) {
      const tank = new THREE.Mesh(new THREE.CapsuleGeometry(0.32, 1.2, 4, 8), darkMat);
      tank.position.set(0, 0.15, sz);
      this.hull.add(tank);
    }

    // Landing legs: splayed struts + feet, angled out to the sides
    const legMat = darkMat;
    for (const sx of [-1, 1]) {
      const strut = new THREE.Mesh(new THREE.BoxGeometry(0.16, 1.5, 0.16), legMat);
      strut.position.set(sx * 0.95, -1.05, 0);
      strut.rotation.z = -sx * 0.45;
      const foot = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.14, 0.4), legMat);
      foot.position.set(sx * 1.28, -1.72, 0);
      this.hull.add(strut, foot);
    }

    this.flameMat = new THREE.MeshBasicMaterial({
      color: 0xff8a2a, transparent: true, opacity: 0.95,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });
    this.flame = new THREE.Mesh(new THREE.ConeGeometry(0.5, 2.0, 8), this.flameMat);
    this.flame.rotation.x = Math.PI;
    this.flame.position.y = -2.6;
    this.flame.visible = false;

    // Retro puff from the nose (S — pushes the ship backward)
    this.retroFlame = new THREE.Mesh(new THREE.ConeGeometry(0.3, 1.1, 8), this.flameMat);
    this.retroFlame.position.y = 3.4;
    this.retroFlame.visible = false;

    this.hull.add(body, nose, bell, window_, this.flame, this.retroFlame);
    this.group.add(this.hull);
  }

  get pos(): THREE.Vector3 { return this.group.position; }

  /** Nose-relative attitude normalized to [-π, π] (0 = upright). */
  get uprightError(): number {
    return Math.atan2(Math.sin(this.angle), Math.cos(this.angle));
  }

  /**
   * Lunar-lander flight: A/D rotate the rocket's attitude (full 360°),
   * W boosts along the nose, S retro-burns backward against the nose.
   * Returns 'landed-soft' | 'landed-hard' | null.
   * `rotate`: +1 = rotate left (CCW), -1 = right.
   */
  fly(
    dt: number, rotate: number, burn: boolean, retro: boolean,
    world: World, thrustBonus: number,
  ): 'landed-soft' | 'landed-hard' | null {
    // Attitude — unclamped: loops and flips are allowed
    this.angle += rotate * C.ship.rotSpeed * dt;

    // Physics: gravity fades to zero-g in space; thrust along ±nose
    this.vel.y += C.ship.gravity * gravityScaleAt(this.pos.y) * dt;
    if (burn) {
      const t = C.ship.thrust + thrustBonus;
      this.vel.x += -Math.sin(this.angle) * t * dt;
      this.vel.y += Math.cos(this.angle) * t * dt;
    }
    if (retro) {
      const t = C.ship.retroThrust + thrustBonus * 0.5;
      this.vel.x += Math.sin(this.angle) * t * dt;
      this.vel.y += -Math.cos(this.angle) * t * dt;
    }
    this.vel.x *= Math.pow(C.ship.drag, dt * 60);
    this.vel.y *= Math.pow(C.ship.drag, dt * 60);
    this.vel.x = THREE.MathUtils.clamp(this.vel.x, -C.ship.maxSide, C.ship.maxSide);
    this.vel.y = THREE.MathUtils.clamp(this.vel.y, C.ship.maxFall, C.ship.maxRise + thrustBonus);

    this.pos.x += this.vel.x * dt;
    this.pos.y += this.vel.y * dt;
    this.pos.x = THREE.MathUtils.clamp(
      this.pos.x, -C.world.cols / 2 + 3, C.world.cols / 2 - 3);

    // Visual = actual attitude (this IS the control, not decoration),
    // plus a slow yaw hint so tanks/legs catch light in 3D.
    this.hull.rotation.z = this.angle;
    this.hull.rotation.y = THREE.MathUtils.lerp(
      this.hull.rotation.y, rotate * 0.3, dt * 4);

    this.flame.visible = burn;
    if (burn) this.flame.scale.y = 0.8 + Math.random() * 0.5;
    this.retroFlame.visible = retro;
    if (retro) this.retroFlame.scale.y = 0.7 + Math.random() * 0.4;

    // Touch down? (legs reach ~1.8 below center)
    const ground = world.surfaceYAt(this.pos.x) + 1.8;
    if (this.pos.y <= ground && this.vel.y <= 0) {
      const hard = this.vel.y < C.ship.safeLandSpeed
        || Math.abs(this.uprightError) > C.ship.safeLandTilt;
      this.pos.y = ground;
      this.vel.set(0, 0);
      this.landed = true;
      this.angle = 0;
      this.flame.visible = false;
      this.hull.rotation.z = 0;
      this.hull.rotation.y = 0;
      return hard ? 'landed-hard' : 'landed-soft';
    }
    return null;
  }

  takeOffReset(): void {
    this.landed = false;
    this.vel.set(0, 2);
  }

  /** Directly pose the attitude (used by the docking sequence). */
  setAttitude(angle: number): void {
    this.angle = angle;
    this.hull.rotation.z = angle;
    this.flame.visible = false;
    this.retroFlame.visible = false;
  }
}
